// EventRow — single row in the OLIVIUM Signal feed.
// Tighter command-center rhythm: small title, mono meta, tiny
// hash chip, status dot with glow.

import { useShell } from '../shell/ShellContext'
import { useNavigate } from 'react-router-dom'
import { SHELL_TOKENS, badgeTone, shortHash, text, timeAgo } from '../shell/styles'
import type { AuditEvent } from '../hooks/types'
import { resolveOliviumLinkedSurface } from '../routes/Olivium/linkedSurface'

interface EventRowProps {
  event: AuditEvent
}

export default function EventRow({ event }: EventRowProps) {
  const { openDetail } = useShell()
  const navigate = useNavigate()
  const tone = badgeTone(event.severity)
  const hash = event.chain_hash || event.event_hash
  const linked = resolveOliviumLinkedSurface(event as unknown as Record<string, unknown>)

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'stretch',
        gap: 12,
        borderBottom: `1px solid ${SHELL_TOKENS.border}`,
      }}
    >
      <button
        type="button"
        onClick={() => openDetail(event)}
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          padding: '11px 14px',
          background: 'transparent',
          color: SHELL_TOKENS.ink,
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          fontFamily: 'inherit',
          flex: 1,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <span
            style={{
              width: 7,
              height: 7,
              borderRadius: 999,
              background: tone.text,
              boxShadow: `0 0 10px ${tone.text}66`,
              flexShrink: 0,
            }}
          />
          <div style={{ minWidth: 0 }}>
            <div style={{ ...text.rowTitle, fontSize: 12, marginBottom: 2 }}>
              {event.action || event.event_type || 'event'}
            </div>
            <div style={text.rowMeta}>
              {event.actor_role || event.actor_id || 'system'} · {event.resource_type || 'resource'} · {timeAgo(event.created_at)}
            </div>
          </div>
        </div>

        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 58,
              padding: '3px 8px',
              borderRadius: 3,
              border: `1px solid ${tone.border}`,
              background: tone.bg,
              color: tone.text,
              fontSize: 8,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              fontFamily: 'var(--font-mono)',
            }}
          >
            {event.severity || 'info'}
          </div>
          <div
            style={{
              fontSize: 9,
              color: SHELL_TOKENS.inkFaint,
              marginTop: 4,
              fontFamily: 'var(--font-mono)',
            }}
          >
            {shortHash(hash)}
          </div>
        </div>
      </button>

      {linked && (
        <button
          type="button"
          onClick={() => navigate(linked.path)}
          style={{
            margin: '11px 14px 11px 0',
            alignSelf: 'center',
            padding: '6px 10px',
            borderRadius: 999,
            border: `1px solid ${SHELL_TOKENS.brandLine}`,
            background: SHELL_TOKENS.brandSoft,
            color: SHELL_TOKENS.brand,
            cursor: 'pointer',
            fontSize: 10,
            fontFamily: 'var(--font-mono)',
            whiteSpace: 'nowrap',
          }}
        >
          {linked.label}
        </button>
      )}
    </div>
  )
}
